import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Modal,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { authentication } from "@microsoft/teams-js";
import CloseIcon from "@mui/icons-material/Close";

export default function JoinPage() {
  const [joinUrl, setJoinUrl] = useState("");
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isInputLocked, setIsInputLocked] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const navigate = useNavigate();

  const handleConfirm = async () => {
    if (!joinUrl) {
      alert("Please enter your Teams join URL.");
      return;
    }

    setIsInputLocked(true);
    setIsAuthenticating(true);

    const encodedJoinUrl = encodeURIComponent(joinUrl);
    const popupUrl = `${process.env.REACT_APP_TEAMS_APP_FRONTEND_URL}/auth-popup?joinUrl=${encodedJoinUrl}`;

    try {
      const result = await authentication.authenticate({
        url: popupUrl,
        width: 600,
        height: 535,
      });

      // start a meeting in backend /auth/callback
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/microsoft/auth/callback?code=${result}&state=joinUrl=${encodedJoinUrl}`,
        { method: "GET" },
      );

      const data = await response.json();
      const meetingId = data.meetingId;

      navigate(
        `/dashboard?joinUrl=${encodedJoinUrl}&meetingId=${encodeURIComponent(
          meetingId,
        )}`,
      );
    } catch (error) {
      console.error("Auth failed:", error);
      alert("Authentication failed: " + error);
      setIsInputLocked(false);
      setIsAuthenticating(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 2,
        px: 2,
      }}
    >
      <Typography variant="h5" fontWeight="bold">
        Enter Your Teams Join URL
      </Typography>
      <TextField
        fullWidth
        label="Teams Join URL"
        value={joinUrl}
        onChange={(e) => setJoinUrl(e.target.value)}
        placeholder="Paste your Teams join link here"
        disabled={isInputLocked}
        sx={{
          mt: 2,
          backgroundColor: isInputLocked ? "#f0f0f0" : "inherit",
        }}
      />
      <Typography
        variant="body2"
        color="primary"
        sx={{ cursor: "pointer", textDecoration: "underline" }}
        onClick={() => setShowHelpModal(true)}
      >
        Find the Teams join URL here
      </Typography>
      <Button
        variant="contained"
        onClick={handleConfirm}
        disabled={isAuthenticating}
        sx={{ position: "relative", minWidth: 200 }}
      >
        {isAuthenticating ? "Authenticating..." : "Confirm and Authenticate"}
        {isAuthenticating && (
          <CircularProgress
            size={20}
            sx={{
              color: "white",
              position: "absolute",
              right: 16,
              top: "50%",
              marginTop: "-10px",
            }}
          />
        )}
      </Button>

      <Modal open={showHelpModal} onClose={() => setShowHelpModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            maxWidth: 600,
            width: "90%",
          }}
        >
          <IconButton
            onClick={() => setShowHelpModal(false)}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" fontWeight="bold" mb={2}>
            Teams Join Link
          </Typography>

          <Typography variant="body1" mb={1}>
            1. Click <strong>'People'</strong> on the tab bar
          </Typography>
          <Typography variant="body1" mb={1}>
            2. Click <strong>'Share Invite'</strong>
          </Typography>
          <Typography variant="body1" mb={2}>
            3. Click <strong>'Copy meeting link'</strong>
          </Typography>

          <img
            src="/images/teams-join-help.png"
            alt="Teams join link help"
            style={{ width: "100%", borderRadius: 8, marginBottom: 20 }}
          />

          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <Button
              variant="contained"
              onClick={() => setShowHelpModal(false)}
              sx={{ minWidth: 120 }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
